/**
 * Agent Task Detail Page
 *
 * Shows full task details, description (as markdown), event timeline, and action buttons.
 *
 * @module features/tasks/pages/TaskDetail
 */

import React from 'react';
import { Page, PageHeader, Text, Button, Flex, Box, Dialog, DialogTrigger, DialogContent, DialogFooter, useToast, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import { Check, X, Play, UserCircle, Trash } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { useTask, useCompleteTask, useCancelTask, useTriggerTask, useClaimTask, useDeleteTask } from '../hooks/useTasks';
import { useTaskRepos } from '../hooks/useTaskRepos';
import { taskDetailRoute } from '../../../router';
import { STATUS_VARIANTS, STATUS_LABELS } from '../constants';
import { EditTaskDialog } from '../components/EditTaskDialog';

export const TaskDetail: React.FC = () => {
  const { id } = taskDetailRoute.useParams();
  const { data, isLoading } = useTask(id);
  const completeTask = useCompleteTask();
  const cancelTask = useCancelTask();
  const triggerTask = useTriggerTask();
  const claimTask = useClaimTask();
  const deleteTask = useDeleteTask();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  const task = data?.data;
  const { data: reposData } = useTaskRepos();
  const repoFullName = React.useMemo(() => {
    if (!task) return null;
    return reposData?.data?.find((r) => r.id === task.propertyGithubRepoId)?.repoFullName ?? null;
  }, [reposData, task]);

  if (isLoading) {
    return (
      <Page>
        <Text>Loading...</Text>
      </Page>
    );
  }

  if (!task) {
    return (
      <Page>
        <Text>Task not found</Text>
      </Page>
    );
  }

  const canEdit =
    task.status !== 'completed' && task.status !== 'canceled' && task.status !== 'expired';
  const canComplete = task.status === 'actionable' || task.status === 'in_progress';
  const canCancel = task.status === 'waiting' || task.status === 'actionable' || task.status === 'in_progress';
  const canTrigger = task.status === 'waiting';
  const canClaim = task.status === 'actionable';

  return (
    <Page>
      <PageHeader
        title={task.title}
        actions={
          <Flex gap="sm">
            {canEdit && <EditTaskDialog task={task} />}
            <Button
              variant="outline"
              iconLeft={<Trash size={16} />}
              onPress={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
            {canClaim && (
              <Button
                variant="outline"
                iconLeft={<UserCircle size={16} />}
                onPress={() => claimTask.mutate(task.id)}
                isDisabled={claimTask.isPending}
              >
                Claim
              </Button>
            )}
            {canTrigger && (
              <Button
                variant="outline"
                iconLeft={<Play size={16} />}
                onPress={() => triggerTask.mutate(task.id)}
                isDisabled={triggerTask.isPending}
              >
                Trigger
              </Button>
            )}
            {canCancel && (
              <Button
                variant="outline"
                iconLeft={<X size={16} />}
                onPress={() => cancelTask.mutate(task.id)}
                isDisabled={cancelTask.isPending}
              >
                Cancel
              </Button>
            )}
            {canComplete && (
              <Button
                variant="primary"
                iconLeft={<Check size={16} />}
                onPress={() => completeTask.mutate({ id: task.id })}
                isDisabled={completeTask.isPending}
              >
                Complete
              </Button>
            )}
          </Flex>
        }
      />

      <Flex gap="xl" direction="column">
        {/* Status and metadata */}
        <Flex gap="md" wrap="wrap">
          <TagBadge name={STATUS_LABELS[task.status]} color={badgeVariantToTagColor(STATUS_VARIANTS[task.status])} hideDot />
          <TagBadge name={task.triggerType} color="gray" hideDot />
          {task.priority > 0 && (
            <TagBadge name={<>{task.priority === 2 ? 'Critical' : 'High'} Priority</>} color={badgeVariantToTagColor(task.priority === 2 ? 'destructive' : 'warning')} hideDot />
          )}
        </Flex>

        {/* Metadata grid */}
        <Box className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 rounded-lg border border-border bg-card">
          <div>
            <Text size="xs" className="text-muted-foreground">Source Commit</Text>
            {task.sourceCommit && repoFullName ? (
              <a
                href={`https://github.com/${repoFullName}/commit/${task.sourceCommit}`}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-mono underline"
              >
                {task.sourceCommit.slice(0, 7)}
              </a>
            ) : (
              <Text size="sm" className="font-mono">{task.sourceCommit?.slice(0, 7) ?? '-'}</Text>
            )}
          </div>
          <div>
            <Text size="xs" className="text-muted-foreground">Resolution</Text>
            {task.resolutionPrNumber !== null && task.resolutionPrNumber !== undefined ? (
              repoFullName ? (
                <a
                  href={`https://github.com/${repoFullName}/pull/${task.resolutionPrNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm underline"
                >
                  #{task.resolutionPrNumber}
                </a>
              ) : (
                <Text size="sm">#{task.resolutionPrNumber}</Text>
              )
            ) : task.resolutionCommit ? (
              repoFullName ? (
                <a
                  href={`https://github.com/${repoFullName}/commit/${task.resolutionCommit}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-mono underline"
                >
                  {task.resolutionCommit.slice(0, 7)}
                </a>
              ) : (
                <Text size="sm" className="font-mono">{task.resolutionCommit.slice(0, 7)}</Text>
              )
            ) : (
              <Text size="sm" className="font-mono">-</Text>
            )}
          </div>
          <div>
            <Text size="xs" className="text-muted-foreground">Assigned To</Text>
            <Text size="sm">{task.assignedTo ?? '-'}</Text>
          </div>
          <div>
            <Text size="xs" className="text-muted-foreground">Created</Text>
            <Text size="sm">{new Date(task.createdAt).toLocaleString()}</Text>
          </div>
          {task.triggeredAt && (
            <div>
              <Text size="xs" className="text-muted-foreground">Triggered</Text>
              <Text size="sm">{new Date(task.triggeredAt).toLocaleString()}</Text>
            </div>
          )}
          {task.completedAt && (
            <div>
              <Text size="xs" className="text-muted-foreground">Completed</Text>
              <Text size="sm">{new Date(task.completedAt).toLocaleString()}</Text>
            </div>
          )}
          {Object.keys(task.triggerConfig).length > 0 && (
            <div className="col-span-2">
              <Text size="xs" className="text-muted-foreground">Trigger Config</Text>
              <Text size="sm" className="font-mono">{JSON.stringify(task.triggerConfig)}</Text>
            </div>
          )}
          {task.tags.length > 0 && (
            <div className="col-span-2">
              <Text size="xs" className="text-muted-foreground">Tags</Text>
              <Flex gap="xs">
                {task.tags.map((tag) => (
                  <TagBadge name={tag} color="gray" hideDot key={tag} />
                ))}
              </Flex>
            </div>
          )}
        </Box>

        {/* Description */}
        <Box className="p-4 rounded-lg border border-border bg-card">
          <Text size="sm" weight="medium" className="mb-2">Description</Text>
          <pre className="whitespace-pre-wrap text-sm font-mono text-foreground">{task.description}</pre>
        </Box>

        {/* Event timeline */}
        {task.events && task.events.length > 0 && (
          <Box className="p-4 rounded-lg border border-border bg-card">
            <Text size="sm" weight="medium" className="mb-3">Event History</Text>
            <div className="space-y-2">
              {task.events.map((event) => (
                <Flex key={event.id} gap="md" align="center">
                  <Text size="xs" className="text-muted-foreground min-w-[140px]">
                    {new Date(event.createdAt).toLocaleString()}
                  </Text>
                  <TagBadge name={event.eventType} color="gray" hideDot />
                  <Text size="xs" className="text-muted-foreground">
                    {event.actor ?? 'system'}
                  </Text>
                </Flex>
              ))}
            </div>
          </Box>
        )}
      </Flex>

      <DialogTrigger isOpen={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <Button className="hidden">Delete</Button>
        <Dialog>
          <DialogContent>
            <Text>
              Are you sure you want to delete this task? This action cannot be undone.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button variant="outline" onPress={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onPress={async () => {
                try {
                  await deleteTask.mutateAsync(task.id);
                  toast.success('Task deleted');
                  setDeleteDialogOpen(false);
                  void navigate({ to: '/tasks' });
                } catch {
                  toast.error('Failed to delete task');
                }
              }}
              isDisabled={deleteTask.isPending}
            >
              {deleteTask.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </Page>
  );
};
